import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, HeartPulse, ArrowRight, Bell, Activity, Filter, Database } from 'lucide-react';
import { useT } from '../../i18n';
import { useOs2, useRangeLabel, ScopeTag, PageHead, Card, money, InfoTip, Fade, SEVERITY, timeAgo, LoadingState, LiveDot } from './os2ui';
import { MapPin } from 'lucide-react';
import { RachelGreeting } from '../../components/RachelGreeting';
import AgentAdviceRail from '../../components/AgentAdviceRail';
import OverviewStory from './OverviewStory';

/* Executive Overview — EL HOME. "How is Kitxens doing today?" — the summary of EVERYTHING. */

export default function OverviewPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { data: d, err, reload, fetchedAt, loading } = useOs2('/overview');
  const { data: hdr } = useOs2('/header');
  const { label } = useRangeLabel(isEs);

  if (err) return <p className="text-sm text-red-600" data-testid="os2-overview-error">{err}</p>;
  if (!d) return <LoadingState />;

  const h = d.health || {};
  const tone = h.score >= 80 ? 'emerald' : h.score >= 60 ? 'amber' : 'red';
  const toneText = { emerald: 'text-emerald-600', amber: 'text-amber-600', red: 'text-red-600' }[tone];
  const toneBar = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500' }[tone];
  const activity = d.recent_activity || [];
  const notifs = activity.filter((a) => a.severity !== 'info').slice(0, 6);
  const alert = notifs.find((a) => ['warn', 'warning', 'critical', 'error'].includes(a.severity));
  const rachelEs = <>Hoy hay <b>{(d.priorities || []).length}</b> {(d.priorities || []).length === 1 ? 'prioridad' : 'prioridades'} en tu radar y llevas <b>{d.leads_today ?? 0}</b> {(d.leads_today ?? 0) === 1 ? 'lead nuevo' : 'leads nuevos'}. Salud de la empresa: <b>{h.score ?? '—'}/100</b>.{alert ? <> Atención: <b>{alert.title_es}</b>{alert.ts && <> ({timeAgo(alert.ts, true)})</>}.</> : ' Sin incidencias abiertas — todo operativo.'}{(d.priorities || []).length > 0 && <> Empezaría por: <b>{String(d.priorities[0]).replace(/^[^\w¡¿"“]+\s*/, '')}</b></>} ¿Te muestro los detalles?</>;
  const rachelEn = <>There {(d.priorities || []).length === 1 ? 'is' : 'are'} <b>{(d.priorities || []).length}</b> {(d.priorities || []).length === 1 ? 'priority' : 'priorities'} on your radar and <b>{d.leads_today ?? 0}</b> new {(d.leads_today ?? 0) === 1 ? 'lead' : 'leads'} today. Company health: <b>{h.score ?? '—'}/100</b>.{alert ? <> Heads up: <b>{alert.title_en}</b>{alert.ts && <> ({timeAgo(alert.ts, false)})</>}.</> : ' No open incidents — all systems operational.'}{(d.priorities || []).length > 0 && <> I would start with: <b>{String(d.priorities[0]).replace(/^[^\w"“]+\s*/, '')}</b></>} Would you like me to show you the details?</>;

  return (
    <div data-testid="os2-overview">
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="os2-rachel">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F77F11]">Kitxens AI OS · Command Center</p>
          <h1 className="text-[20px] font-semibold tracking-tight text-gray-900">
            {isEs ? `Bienvenido, ${hdr?.name || 'Oscar'} — tu equipo ya revisó el negocio` : `Welcome, ${hdr?.name || 'Oscar'} — your team already reviewed the business`}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {h.score != null && (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-bold ${(h.score ?? 0) >= 80 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-testid="ov-health-pill">
              <span className={`w-2 h-2 rounded-full ${(h.score ?? 0) >= 80 ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
              {(h.score ?? 0) >= 80 ? (isEs ? 'Empresa saludable' : 'Company healthy') : (isEs ? 'Requiere atención' : 'Needs attention')} · {h.score}/100
            </span>
          )}
          <Link to="/admin/ai-os/autonomy?tab=team" data-testid="ov-talk-rachel"
            className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-4 py-1.5 text-[12px] font-bold text-white hover:bg-gray-800 transition-colors">
            {isEs ? 'Hablar con Rachel' : 'Talk to Rachel'} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
      <AgentAdviceRail audience="admin" isEs={isEs} className="mt-4" />
      <div className="mt-6" />
      <PageHead
        title={isEs ? 'Resumen Ejecutivo' : 'Executive Overview'}
        question={isEs ? '¿Cómo va Kitxens hoy? — el resumen de TODO.' : 'How is Kitxens doing today? — the summary of everything.'}
        fresh={{ at: fetchedAt, onReload: reload, loading, isEs }}
        module="AI OS"
      />

      {/* State + Event Bus — one compact line each, side by side, immediate pulse */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <Fade delay={0}>
          <div className="h-full rounded-xl bg-white border border-gray-200 px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px]" data-testid="ov-today-ribbon">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-400"><LiveDot color="orange" /> {isEs ? 'Estado' : 'State'}</span>
            <Link to="/admin/ai-os/pipeline?tab=sales" className="text-gray-600 hover:text-[#d8651a] transition-colors" data-testid="ov-state-leads"><b className="text-gray-900 tabular-nums">{d.leads_today}</b> {isEs ? 'leads hoy' : 'leads today'}</Link>
            <Link to="/admin/ai-os/pipeline?tab=sales" className="text-gray-600 hover:text-[#d8651a] transition-colors" data-testid="ov-state-meetings"><b className="text-gray-900 tabular-nums">{d.meetings}</b> {isEs ? 'reuniones' : 'meetings'}</Link>
            <Link to="/admin/ai-os/pipeline?tab=customers" className="text-gray-600 hover:text-[#d8651a] transition-colors" data-testid="ov-state-customers"><b className="text-gray-900 tabular-nums">{d.customers}</b> {isEs ? 'clientes' : 'customers'}</Link>
            <Link to="/admin/ai-os/pipeline" className="text-gray-600 hover:text-[#d8651a] transition-colors" data-testid="ov-state-opportunities"><b className="text-gray-900 tabular-nums">{d.open_opportunities}</b> {isEs ? 'oportunidades' : 'opportunities'}</Link>
            <span className="ml-auto inline-flex items-center gap-1 text-[9.5px] text-gray-400"><Database className="w-2.5 h-2.5" aria-hidden="true" /> Kitxens DB</span>
          </div>
        </Fade>
        {d.event_bus && (
          <Fade delay={0.05}>
            <div className="h-full rounded-xl bg-white border border-gray-200 px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px]" data-testid="ov-eventbus-card">
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Event Bus</span>
              <span className={`inline-flex items-center gap-1.5 font-bold ${d.event_bus.status === 'ok' ? 'text-emerald-600' : d.event_bus.status === 'error' ? 'text-red-600' : 'text-gray-400'}`} data-testid="ov-eventbus-status">
                <LiveDot color={d.event_bus.status === 'ok' ? 'emerald' : d.event_bus.status === 'error' ? 'red' : 'gray'} />
                {d.event_bus.status === 'ok' ? 'Online' : d.event_bus.status === 'error' ? 'DLQ' : 'Idle'}
              </span>
              <span className="text-gray-600 min-w-0 truncate max-w-[280px]">{d.event_bus.last_event ? `${d.event_bus.last_event.type} · ${timeAgo(d.event_bus.last_event.created_at, isEs)}` : '—'}</span>
              <span className="text-gray-600"><b className="tabular-nums text-gray-900">{d.event_bus.events_today}</b> {isEs ? 'hoy' : 'today'}</span>
              <span className="text-gray-600">DLQ <b className={`tabular-nums ${d.event_bus.dlq > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="ov-eventbus-dlq">{d.event_bus.dlq}</b></span>
              <a href="/admin/ai-os/system-health" className="ml-auto text-[11px] font-semibold text-[#F77F11] hover:underline" data-testid="ov-eventbus-health-link">
                {isEs ? 'Operations →' : 'Operations →'}
              </a>
            </div>
          </Fade>
        )}
      </div>

      {/* Hero row: Health · Revenue Today · MRR · Pipeline */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Fade delay={0}>
          <Link to="/admin/ai-os/system-health" className="block h-full group" data-testid="ov-health">
            <Card className="p-5 h-full border-l-4 group-hover:shadow-md transition-all duration-300" style={{ borderLeftColor: tone === 'emerald' ? '#10b981' : tone === 'amber' ? '#f59e0b' : '#ef4444' }}>
              <p className="text-xs tracking-wider uppercase text-gray-500 font-medium flex items-center gap-1.5">
                <HeartPulse className={`w-3.5 h-3.5 ${toneText}`} aria-hidden="true" /> {isEs ? 'Salud de la empresa' : 'Company health'}
                <InfoTip k="health" isEs={isEs} />
              </p>
              <p className={`mt-2 text-3xl font-semibold tracking-tight tabular-nums ${toneText}`}>{h.score}<span className="text-sm text-gray-300 font-normal">/100</span></p>
              <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full rounded-full ${toneBar} transition-[width] duration-1000`} style={{ width: `${h.score}%` }} />
              </div>
              <p className="mt-2 text-xs text-gray-400">{(h.factors || []).length ? h.factors[0].label : (isEs ? 'Sin incidencias ✓' : 'No issues ✓')}</p>
            </Card>
          </Link>
        </Fade>
        <Fade delay={0.06}>
          <Link to="/admin/ai-os/finance" className="block h-full group" data-testid="ov-revenue">
            <Card className="p-5 h-full group-hover:border-[#F77F11]/50 group-hover:shadow-md transition-all duration-300">
              <p className="text-xs tracking-wider uppercase text-gray-500 font-medium flex items-center gap-1.5">
                <span className="inline-flex items-center">{isEs ? 'Ingresos hoy' : 'Revenue today'}<InfoTip k="revenue" isEs={isEs} /></span>
                <ScopeTag current isEs={isEs} />
              </p>
              <p className="mt-2 text-3xl font-semibold tracking-tight tabular-nums text-[#F77F11]">{money(d.revenue_today)}</p>
              <p className="mt-2 text-xs text-gray-400">{money(d.period.revenue_received)} · {label} — {money(d.revenue.total)} total</p>
              <p className="mt-1 inline-flex items-center gap-1 text-[10px] text-gray-400"><Database className="w-2.5 h-2.5" aria-hidden="true" /> {isEs ? 'Fuente' : 'Source'}: Stripe · <span className="text-[#F77F11] font-semibold">{isEs ? 'Ver Finanzas →' : 'View Finance →'}</span></p>
            </Card>
          </Link>
        </Fade>
        <Fade delay={0.12}>
          <Link to="/admin/ai-os/finance" className="block h-full group" data-testid="ov-mrr">
            <Card className="p-5 h-full group-hover:border-[#F77F11]/50 group-hover:shadow-md transition-all duration-300">
              <p className="text-xs tracking-wider uppercase text-gray-500 font-medium flex items-center gap-1.5">
                <span className="inline-flex items-center">MRR<InfoTip k="mrr" isEs={isEs} /></span>
                <ScopeTag current isEs={isEs} />
              </p>
              <p className="mt-2 text-3xl font-semibold tracking-tight tabular-nums text-gray-900">{money(d.revenue.mrr)}</p>
              <p className="mt-2 text-xs text-gray-400">{d.revenue.paying_customers} {isEs ? 'suscripciones activas' : 'active subscriptions'} · ARR {money(d.revenue.mrr * 12)}</p>
              <p className="mt-1 inline-flex items-center gap-1 text-[10px] text-gray-400"><Database className="w-2.5 h-2.5" aria-hidden="true" /> {isEs ? 'Fuente' : 'Source'}: Stripe · <span className="text-[#F77F11] font-semibold">{isEs ? 'Ver Finanzas →' : 'View Finance →'}</span></p>
            </Card>
          </Link>
        </Fade>
        <Fade delay={0.18}>
          <Link to="/admin/ai-os/pipeline" className="block h-full group" data-testid="ov-pipeline">
            <Card className="p-5 h-full group-hover:border-[#F77F11]/50 group-hover:shadow-md transition-all duration-300">
              <p className="text-xs tracking-wider uppercase text-gray-500 font-medium flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-[#F77F11]" aria-hidden="true" /> Pipeline<InfoTip k="opportunities" isEs={isEs} />
                <ScopeTag current isEs={isEs} />
              </p>
              <p className="mt-2 text-3xl font-semibold tracking-tight tabular-nums text-gray-900">{d.open_opportunities}</p>
              <p className="mt-2 text-xs text-[#F77F11] font-semibold inline-flex items-center gap-1">
                {isEs ? 'Ver Revenue Pipeline' : 'View Revenue Pipeline'} <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </p>
            </Card>
          </Link>
        </Fade>
      </div>

      <OverviewStory isEs={isEs} />

      {/* Priorities + AI recommendations */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Fade delay={0.28}>
          <Card className="p-6 h-full" data-testid="ov-priorities">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <h2 className="text-[15px] font-semibold tracking-tight text-gray-900 flex items-center gap-2">
                {isEs ? 'Prioridades de hoy' : "Today's priorities"}
                {d.production_status && (
                  <span data-testid="ov-production-status" title={d.production_status.reason || ''}
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10.5px] font-bold tracking-wide ${
                      d.production_status.status === 'GO' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : d.production_status.status === 'NO-GO' ? 'bg-red-50 text-red-600 border-red-300'
                          : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                    {d.production_status.status === 'GO' ? 'PRODUCTION GO ✅'
                      : d.production_status.status === 'NO-GO' ? 'PRODUCTION NO-GO ❌'
                        : (isEs ? 'SIN PREFLIGHT 24H' : 'NO PREFLIGHT 24H')}
                  </span>
                )}
              </h2>
              <span className="text-[11px] text-gray-400">{isEs ? 'por Eva · datos reales' : 'by Eva · real data'}</span>
            </div>
            {(d.priorities || []).length ? d.priorities.map((p, i) => (
              <div key={i} className="flex gap-3 py-2 border-b border-gray-100 last:border-0">
                <span className="w-5 h-5 shrink-0 rounded-full bg-[#FFF7ED] border border-orange-200 text-[#F77F11] text-[11px] font-bold flex items-center justify-center tabular-nums">{i + 1}</span>
                <p className="text-[13.5px] text-gray-700 leading-relaxed">{p}</p>
              </div>
            )) : <p className="text-sm text-gray-400">{isEs ? 'Sin prioridades pendientes 🎉' : 'No pending priorities 🎉'}</p>}
          </Card>
        </Fade>
        <Fade delay={0.34}>
          <Card className="p-6 h-full" data-testid="ov-recommendations">
            <h2 className="text-[15px] font-semibold tracking-tight text-gray-900 flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#F77F11]" aria-hidden="true" /> {isEs ? 'Recomendaciones AI' : 'AI recommendations'}
            </h2>
            {d.recommendations?.length ? d.recommendations.map((r, i) => (
              <div key={i} className="py-2 border-b border-gray-100 last:border-0">
                <p className="text-[13.5px] text-gray-700 leading-relaxed">{r.title}</p>
                <p className="text-[11px] text-gray-400 capitalize mt-0.5">{[r.agent, r.impact].filter(Boolean).join(' · ')}</p>
              </div>
            )) : (
              <p className="text-sm text-gray-400">{isEs ? 'Sin recomendaciones nuevas — los agentes generan recomendaciones con actividad real.' : 'No new recommendations — agents generate them from real activity.'}</p>
            )}
          </Card>
        </Fade>
      </div>

      {/* Recent Activity + Notifications */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Fade delay={0.4}>
          <Card className="p-6 h-full" data-testid="ov-activity">
            <h2 className="text-[15px] font-semibold tracking-tight text-gray-900 flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-[#F77F11]" aria-hidden="true" /> {isEs ? 'Actividad reciente' : 'Recent activity'}
            </h2>
            {activity.slice(0, 8).map((a, i) => (
              <div key={i} className="flex items-start gap-2.5 py-1.5 border-b border-gray-100 last:border-0">
                <span className="text-[15px] leading-none mt-0.5">{a.icon}</span>
                <p className="text-[13px] text-gray-700 min-w-0 truncate">
                  {isEs ? a.title_es : a.title_en}{a.detail && <span className="text-gray-400"> · {a.detail}</span>}
                </p>
                <span className="ml-auto shrink-0 text-[11px] text-gray-300 tabular-nums">{timeAgo(a.at, isEs)}</span>
              </div>
            ))}
          </Card>
        </Fade>
        <Fade delay={0.46}>
          <Card className="p-6 h-full" data-testid="ov-notifications">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[15px] font-semibold tracking-tight text-gray-900 flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#F77F11]" aria-hidden="true" /> {isEs ? 'Notificaciones' : 'Notifications'}
              </h2>
              <Link to="/admin/ai-os/notifications" className="text-[12px] font-semibold text-[#F77F11] hover:text-[#d8651a]" data-testid="ov-goto-notifications">
                {isEs ? 'Ver todas →' : 'View all →'}
              </Link>
            </div>
            {notifs.length ? notifs.map((n, i) => (
              <div key={i} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 mb-1.5 ${SEVERITY[n.severity]}`}>
                <span className="text-[15px] leading-none mt-0.5">{n.icon}</span>
                <p className="text-[12.5px] font-medium min-w-0 truncate">{isEs ? n.title_es : n.title_en}{n.detail && <span className="opacity-70 font-normal"> · {n.detail}</span>}</p>
                <span className="ml-auto shrink-0 text-[11px] opacity-60 tabular-nums">{timeAgo(n.at, isEs)}</span>
              </div>
            )) : <p className="text-sm text-gray-400">{isEs ? 'Nada urgente ✓' : 'Nothing urgent ✓'}</p>}
          </Card>
        </Fade>
      </div>
    </div>
  );
}
